import { useDirection } from "@radix-ui/react-direction";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { getLanguageName } from "@/features/localisation/i18n";
import { setIsLanguageSelectorDialogOpen } from "@/features/localisation/store/language-selector-slice";
import { cn } from "@/lib/utils";
import { useAppDispatch } from "@/store";

import { useStudyLanguages } from "../hooks/use-study-languages";

interface LanguageButtonProps {
  className?: string;
  variant?: "dropdown" | "settings";
}

export const LanguageButton = ({
  className,
  variant = "dropdown",
}: LanguageButtonProps) => {
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const direction = useDirection();

  const { studyLanguages } = useStudyLanguages();

  const currentLanguageText = useMemo(() => {
    if (i18n.language) {
      const name = getLanguageName(i18n.language);

      return name;
    }
  }, [i18n.language]);

  const handleFlagClick = () => {
    dispatch(setIsLanguageSelectorDialogOpen({ isOpen: true }));
  };

  // If we only have one language for study we don't want to show the language button
  if (studyLanguages?.length === 1) return null;

  if (variant === "settings") {
    return (
      <button
        type="button"
        onClick={handleFlagClick}
        data-testid="language-selector-button"
        className={cn(
          "flex items-center gap-x-2 leading-loose text-foreground",
          className
        )}
      >
        <span>{currentLanguageText}</span>
        {direction === "rtl" ? <ChevronLeft /> : <ChevronRight />}
      </button>
    );
  }

  return (
    <div className="flex justify-center items-center">
      <Button
        className={cn("text-white font-semibold", className)}
        onClick={handleFlagClick}
        variant="ghost"
        data-testid="language-selector-button"
      >
        {currentLanguageText}
        <ChevronDown />
      </Button>
    </div>
  );
};
